import { useEffect, useLayoutEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import PublicLogoLink from "../../components/PublicLogoLink";
import { acceptInvitation, validateInvitation, INVITATION_UNAVAILABLE_MESSAGE } from "../../services/auth";

export default function AcceptInvitation() {
    const navigate = useNavigate();
    const location = useLocation();
    const invitationPath = location.pathname;
    const [token, setToken] = useState(() => new URLSearchParams(location.search).get("token"));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitting = useRef(false);
    const [networkError, setNetworkError] = useState("");
    const [attempt, setAttempt] = useState(0);

    useLayoutEffect(() => {
        // Capture once in component memory, then scrub the whole query/hash
        // before paint. Do not propagate invitation parameters to another route.
        window.history.replaceState(window.history.state, "", invitationPath);
        navigate(invitationPath, { replace: true });
    }, []);
    const [invitation, setInvitation] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isInvalid, setIsInvalid] = useState(false);
    const [invalidMessage, setInvalidMessage] = useState(
        INVITATION_UNAVAILABLE_MESSAGE
    );
    const [isComplete, setIsComplete] = useState(false);
    const [successMessage, setSuccessMessage] = useState(
        "Your password has been saved. You can now sign in."
    );
    const [form, setForm] = useState({
        password: "",
        confirmPassword: "",
    });

    useEffect(() => {
        let active = true;
        async function loadInvitation() {
            if (isComplete) return;
            setIsLoading(true);
            setNetworkError("");
            if (!token) {
                setInvalidMessage(INVITATION_UNAVAILABLE_MESSAGE);
                setIsInvalid(true);
                setIsLoading(false);
                return;
            }

            const response = await validateInvitation(token);
            if (!active) return;
            if (
                response.success &&
                response.data &&
                response.data.success === true
            ) {
                setInvitation(response.data);
            } else {
                if (!response.status || response.status >= 500 || response.status === 429) {
                    setNetworkError(response.message || "Invitation service is temporarily unavailable.");
                } else {
                    setInvalidMessage(response.message || INVITATION_UNAVAILABLE_MESSAGE);
                    setIsInvalid(true);
                    setToken(null);
                }
            }
            setIsLoading(false);
        }

        loadInvitation();
        return () => { active = false; };
    }, [attempt]);

    async function submitInvitation() {
        if (submitting.current || !token || !invitation || isInvalid || isComplete) return;
        if (form.password.trim() === "" || form.confirmPassword === "") {
            toast.error("Password and confirm password are required.");
            return;
        }

        if (new TextEncoder().encode(form.password).length > 72) {
            toast.error("Password must be at most 72 UTF-8 bytes.");
            return;
        }

        if (form.password !== form.confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }

        submitting.current = true;
        setIsSubmitting(true);
        try {
            const response = await acceptInvitation(token, form.password);
            if (response.success && response.data?.success === true) {
                setSuccessMessage("Your password has been saved. You can now sign in.");
                setIsComplete(true);
                setToken(null);
                setForm({ password: "", confirmPassword: "" });
            } else {
                toast.error(response.message || "Unable to accept the invitation. Please contact your administrator.");
                if (!response.status || response.status >= 500) {
                    setNetworkError("Acceptance could not be confirmed. Try signing in with your chosen password, or use password recovery. Contact your administrator if the account is still pending.");
                }
                // A validation/network failure never implies account activation.
                if ([400, 404, 409, 410].includes(response.status)) {
                    setInvalidMessage(response.message);
                    setIsInvalid(true);
                    setToken(null);
                    setForm({ password: "", confirmPassword: "" });
                }
            }
        } finally {
            submitting.current = false;
            setIsSubmitting(false);
        }
    }

    const passwordWasCreatedByAdmin =
        invitation?.passwordWasCreatedByAdmin === true;

    return (
        <section>
            <Toaster />
            <div className="flex h-screen overflow-hidden">
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                        <div className="px-4">
                            <div className="flex items-center justify-between h-16">
                                <PublicLogoLink
                                    src="/logo_white.png"
                                    alt="logo"
                                    className="h-10"
                                />
                            </div>
                        </div>
                    </header>

                    <main>
                        <div className="flex items-center justify-center min-h-full px-4 py-12 sm:px-6 lg:px-8">
                            <div className="w-full max-w-md space-y-8">
                                {isLoading && (
                                    <p className="text-sm text-center text-gray-600">
                                        Loading invitation...
                                    </p>
                                )}

                                {!isLoading && isInvalid && (
                                    <div className="space-y-6 text-center">
                                        <p className="text-lg font-semibold tracking-tight text-black">
                                            {invalidMessage ||
                                                INVITATION_UNAVAILABLE_MESSAGE}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => navigate("/")}
                                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-teal-700 bg-white border border-teal-500 rounded-md hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                        >
                                            Go to sign in
                                        </button>
                                    </div>
                                )}

                                {!isLoading && networkError && (
                                    <div role="alert" className="space-y-4 text-center">
                                        <p>{networkError}</p>
                                        <button type="button" onClick={() => setAttempt((value) => value + 1)} className="btn bg-teal-500 text-white">Check invitation again</button>
                                        <button type="button" onClick={() => navigate("/", { replace: true })} className="btn">Go to sign in</button>
                                    </div>
                                )}
                                {!isLoading && !isInvalid && !networkError && invitation && (
                                    <>
                                        <div>
                                            <div className="flex justify-center w-full">
                                                <PublicLogoLink
                                                    src="/logo_white.png"
                                                    alt="logo"
                                                    className="h-40 w-100"
                                                />
                                            </div>
                                            <h2 className="mt-6 text-3xl font-extrabold text-center text-gray-900">
                                                {passwordWasCreatedByAdmin
                                                    ? "Your account is ready"
                                                    : "Accept invitation"}
                                            </h2>
                                            <p className="mt-3 text-sm text-center text-gray-600">
                                                {invitation.email}
                                            </p>
                                            <p className="mt-6 font-bold tracking-tight text-center text-black text-md">
                                                {passwordWasCreatedByAdmin
                                                    ? "Your administrator has already created a password for your account. You can sign in with that password, or set a new one below."
                                                    : "Create your password to activate your account."}
                                            </p>
                                            <p className="mt-3 text-sm text-center text-gray-600">
                                                {passwordWasCreatedByAdmin
                                                    ? "Setting a new password is optional."
                                                    : "Use the form below to set your password and finish setting up your FirmFlow account."}
                                            </p>
                                        </div>

                                        {isComplete ? (
                                            <div className="space-y-4 text-center">
                                                <p role="status" className="text-sm font-medium text-teal-600">{successMessage}</p>
                                                <button type="button" onClick={() => navigate("/", { replace: true })} className="btn bg-teal-500 text-white">Go to sign in</button>
                                            </div>
                                        ) : (
                                            <div className="mt-8 space-y-6">
                                                <div className="-space-y-px rounded-md shadow-sm">
                                                    <div>
                                                        <label
                                                            htmlFor="password"
                                                            className="sr-only"
                                                        >
                                                            Password
                                                        </label>
                                                        <input
                                                            disabled={isSubmitting}
                                                            id="password"
                                                            name="password"
                                                            type="password"
                                                            autoComplete="new-password"
                                                            value={form.password}
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    password:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            }
                                                            className="relative block w-full px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-none appearance-none rounded-t-md focus:outline-none focus:ring-teal-500 focus:border-teal-500 focus:z-10 sm:text-sm"
                                                            placeholder="Password"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label
                                                            htmlFor="confirmPassword"
                                                            className="sr-only"
                                                        >
                                                            Confirm password
                                                        </label>
                                                        <input
                                                            disabled={isSubmitting}
                                                            id="confirmPassword"
                                                            name="confirmPassword"
                                                            type="password"
                                                            autoComplete="new-password"
                                                            value={
                                                                form.confirmPassword
                                                            }
                                                            onChange={(e) =>
                                                                setForm({
                                                                    ...form,
                                                                    confirmPassword:
                                                                        e.target
                                                                            .value,
                                                                })
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.code ===
                                                                    "Enter"
                                                                ) {
                                                                    submitInvitation();
                                                                }
                                                            }}
                                                            className="relative block w-full px-3 py-2 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-none appearance-none rounded-b-md focus:outline-none focus:ring-teal-500 focus:border-teal-500 focus:z-10 sm:text-sm"
                                                            placeholder="Confirm password"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <button
                                                        disabled={isSubmitting}
                                                        onClick={() =>
                                                            submitInvitation()
                                                        }
                                                        className="relative flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-md group hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                                    >
                                                        {isSubmitting ? "Saving..." : passwordWasCreatedByAdmin
                                                            ? "Set new password"
                                                            : "Create password"}
                                                    </button>
                                                    {passwordWasCreatedByAdmin && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                navigate("/")
                                                            }
                                                            className="relative flex justify-center w-full px-4 py-2 mt-3 text-sm font-medium text-teal-700 bg-white border border-teal-500 rounded-md group hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                                        >
                                                            Go to sign in
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </section>
    );
}
